import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Facebook, Menu, X, Info, Star, PenTool, FileCheck2, Calculator, HardHat, Sun, Sofa } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  Dumaguete PhilX Condev (DPX) / Negros Construction — concept site. A full-
  service design-build firm, presented as a modern professional site (this
  business already has an existing web presence; the concept models what a
  rebuilt, properly maintained site could look like).
*/

const TEAL = "#0E5E5B";
const AMBER = "#E4A73B";
const CREAM = "#F6F4EC";
const INK = "#1A1A1A";

const FB = "https://www.facebook.com/dumagueteconstruction/";
const NAME = "Dumaguete PhilX Condev (DPX)";
const SLUG = "dpx-negros-construction-concept";
const P = "/construction/dpx";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function DpxConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Services", "#services"], ["Our Work", "#work"], ["Service Area", "#area"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Nunito Sans', sans-serif" }} data-testid="dpx-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;500;600;700;800&family=Lexend:wght@500;600;700&display=swap');
        #top h1, #top h2, #top h3 { font-family: 'Lexend', sans-serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-[#082826] text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.2)]" : ""}`} style={{ background: TEAL }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="dpx-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.jpg`} alt={NAME} className="h-11 w-11 rounded-full object-cover" />
            <span className="text-lg text-white font-extrabold tracking-tight">DPX <span className="text-white/70 font-medium text-sm hidden sm:inline">Negros Construction</span></span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-semibold text-white/80 hover:text-white">{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="dpx-header-cta" className="hidden sm:inline-flex rounded-md px-5 py-2.5 text-sm font-bold" style={{ background: AMBER, color: TEAL }}>
            Request a Consultation
          </a>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="dpx-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1" style={{ background: TEAL }}>
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-white font-semibold">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="dpx-mobile-cta" className="block mt-2 text-center rounded-md px-5 py-3 text-sm font-bold" style={{ background: AMBER, color: TEAL }}>Request a Consultation</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[84vh] flex items-end" data-testid="dpx-hero">
        <img src={`${P}/stonework-project.jpg`} alt="A DPX stonework construction project" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${TEAL}22 0%, ${TEAL}CC 65%, ${TEAL}F5 100%)` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-40 w-full">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] drop-shadow" style={{ color: "#FFD9A0" }}>Dumaguete City &middot; Negros Oriental &amp; Siquijor</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-tight text-white font-extrabold">Full-Service Design, Build &amp; Development.</h1>
            <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
              From floor plans and permitting to construction management and solar — DPX carries a project from first design to finished build across Negros Oriental and Siquijor.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="dpx-hero-cta" className="rounded-md px-7 py-3.5 text-sm font-bold" style={{ background: AMBER, color: TEAL }}>Request a Consultation</a>
              <a href="#work" data-testid="dpx-hero-work" className="rounded-md border border-white/40 px-7 py-3.5 text-sm font-bold text-white hover:bg-white/10 transition-colors">See Our Work</a>
            </div>
            <p className="mt-7 text-sm text-white/70" data-testid="dpx-trust-signal">
              <Star size={14} className="inline -mt-0.5" style={{ color: AMBER }} /> Serving Dumaguete City &amp; Siquijor
            </p>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: TEAL }}>Full-Service</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold leading-snug">Services</h2>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-10">
          {[
            [PenTool, "Design & Floor Plans", "Architectural design and floor plans tailored to the lot and the client."],
            [FileCheck2, "Permitting & Utility Hookups", "Building permit processing and utility hookup coordination."],
            [Calculator, "Quantity Surveying & Estimating", "Detailed estimating so budgets are clear before construction starts."],
            [HardHat, "Construction Management", "On-site construction management from groundbreaking to turnover."],
            [Sun, "Solar Installation", "Solar installation for new builds and existing properties."],
            [Sofa, "Kitchen Cabinets & Fit-Out", "Kitchen cabinetry and interior fit-out as part of the finishing stage."],
          ].map(([Icon, t, b]) => (
            <motion.div key={t} {...fadeUp}>
              <Icon size={26} style={{ color: TEAL }} />
              <h3 className="mt-4 text-lg font-bold">{t}</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}90` }}>{b}</p>
            </motion.div>
          ))}
        </div>
        <motion.p {...fadeUp} className="mt-12 max-w-3xl text-sm leading-relaxed" style={{ color: `${INK}AA` }}>
          DPX's solar service grew from construction customers asking for a recommended solar provider, extending the group's design-to-build workflow into a more complete home-construction service.
        </motion.p>
      </section>

      {/* OUR WORK */}
      <section id="work" className="py-20 md:py-28" style={{ background: TEAL }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: AMBER }}>Real Projects</span>
            <h2 className="mt-3 text-4xl sm:text-5xl text-white font-extrabold leading-snug">Our Work</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-8">
            {[
              { img: `${P}/seaview-interior.jpg`, label: "Interior finishing with a sea view" },
              { img: `${P}/foundation-columns.jpg`, label: "Foundation and column work underway" },
            ].map((it) => (
              <motion.div key={it.img} {...fadeUp} data-testid="dpx-work-item" className="overflow-hidden rounded-md">
                <img src={it.img} alt={it.label} className="w-full h-72 object-cover" loading="lazy" />
                <p className="mt-3 text-sm text-white/70">{it.label}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-8 rounded-md overflow-hidden max-w-3xl mx-auto">
            <img src={`${P}/design-render-pool.jpg`} alt="A DPX outdoor living space design concept" className="w-full h-72 object-cover" loading="lazy" />
            <p className="bg-white/10 px-5 py-3 text-xs text-white/70">Design concept for an outdoor living space</p>
          </motion.div>
          <motion.p {...fadeUp} className="mt-10 max-w-2xl mx-auto text-center text-white/80 text-sm leading-relaxed">
            One family's mostly-prefab home in Bacong has held up well for more than a year with no issues — the kind of long-term result we build every project around.
          </motion.p>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section id="area" className="py-16 md:py-20" style={{ background: "#EEE7D4" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl sm:text-3xl font-extrabold" style={{ color: TEAL }}>Serving Dumaguete City, Negros Oriental &amp; Siquijor</h2>
          </motion.div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: TEAL }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl font-extrabold leading-snug">Request a Consultation</h2>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: TEAL }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Address</div><div>Dumaguete City, Negros Oriental</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Mail size={20} style={{ color: TEAL }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Email</div><div className="flex flex-col"><a href="mailto:fb-construction@philx.ph" className="hover:underline">fb-construction@philx.ph</a><a href="mailto:gord@negrosconstruction.com" className="hover:underline">gord@negrosconstruction.com</a></div></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: TEAL }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dpx-facebook-link" className="hover:underline">facebook.com/dumagueteconstruction</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="dpx"
              accent={TEAL}
              accentText="#FFFFFF"
              heading="Request a Consultation"
              subheading="Design, construction, solar or fit-out — tell us about the project and our team will follow up."
              projectTypes={["Design & Floor Plans", "New Construction", "Solar Installation", "Interior Fit-Out", "Other"]}
              messengerUrl={FB}
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: "#082826", color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.jpg`} alt={NAME} className="h-10 w-10 rounded-full object-cover" />
              <span className="text-white text-lg font-extrabold">DPX NEGROS CONSTRUCTION</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">Full-service design, build and development serving Dumaguete City and Siquijor.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">Dumaguete City, Negros Oriental</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Project details and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-md px-6 py-3.5 text-center text-sm font-bold shadow-xl" style={{ background: AMBER, color: TEAL }}>
        Message DPX
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
